import type { TMarket } from '~/src/types/markets'

export const MARKET_SLUG = {
  LITHIUM: 'lithium',
  COPPER: 'copper',
  COBALT: 'cobalt',
  NICKEL: 'nickel',
  NATURAL_GRAPHITE: 'natural-graphite',
  SYNTHETIC_GRAPHITE: 'synthetic-graphite',
  RARE_EARTHS: 'rare-earths',
  MANGANESE: 'manganese',
  PHOSPHATE: 'phosphate',
  FLUORSPAR: 'fluorspar',
  ANODE: 'anode',
  CATHODE: 'cathode',
  SILICON_ANODE: 'silicon-anode',
  LITHIUM_ION_BATTERIES: 'lithium-ion-batteries',
  RECYCLING: 'recycling',
  NEW_TECHNOLOGY: 'new-technology',
  SOLID_STATE: 'solid-state',
  SODIUM_ION_BATTERIES: 'sodium-ion-batteries',
} as const

export const MARKET_TYPE = {
  FORECASTS: 'forecast',
  MARKET_ASSESSMENTS: 'market',
  PRICES_ASSESSMENTS: 'prices',
  SUSTAINABILITY: 'esg',
  WEEKLY_REPORTS: 'weekly-reports',
  CRITICAL_INSIGHTS: 'critical-insights',
} as const

export const MARKET_SERVICE = {
  FORECASTS: 'forecast',
  MARKET_ASSESSMENTS: 'market-assessments',
  PRICES_ASSESSMENTS: 'price-assessments',
  SUSTAINABILITY: 'esg',
} as const

export const MARKETS_SLUG = Object.values(MARKET_SLUG)

export const MARKET_TYPES = Object.values(MARKET_TYPE)

export const MARKET_SLUG_BY_TYPE = {
  [MARKET_TYPE.FORECASTS]: MARKET_TYPE.FORECASTS,
  [MARKET_TYPE.MARKET_ASSESSMENTS]: MARKET_SERVICE.MARKET_ASSESSMENTS,
  [MARKET_TYPE.PRICES_ASSESSMENTS]: MARKET_SERVICE.PRICES_ASSESSMENTS,
} as const

export const SUSTAINABILITY = 'sustainability'

export const BUTTON_DOWNLOAD_ALL = 'button-download-all'

export const PRICES_TAB = {
  TABLE: 'table',
  CHART: 'chart',
  INDEX: 'index',
  CUSTOMISATION_TOOL: 'price-customisation-tool',
} as const

export const METHODOLOGY_TYPE = {
  DOWNLOAD: 'download',
  REDIRECT: 'redirect',
  NONE: 'none',
} as const

/**
 * pm = Price Assessments
 * ma = Market Assessments
 * f = Forecasts
 * esg = ESG - Sustainability
 * capacity = Capacity Database
 */
export const MARKETS: TMarket[] = [
  {
    name: 'Anode',
    slug: MARKET_SLUG.ANODE,
    checkbox: MARKET_SLUG.ANODE,
    hasSustainability: false,
    hasCapacityDatabase: true,
    capacityMethodologyType: METHODOLOGY_TYPE.DOWNLOAD,
    capacityRedirectUrl: null,
    capacityService: MARKET_SERVICE.PRICES_ASSESSMENTS,
  },
  {
    name: 'Cathode',
    slug: MARKET_SLUG.CATHODE,
    checkbox: MARKET_SLUG.CATHODE,
    hasSustainability: false,
    hasCapacityDatabase: true,
    capacityMethodologyType: METHODOLOGY_TYPE.DOWNLOAD,
    capacityRedirectUrl: null,
    capacityService: MARKET_SERVICE.PRICES_ASSESSMENTS,
  },
  {
    name: 'Lithium ion Batteries',
    slug: MARKET_SLUG.LITHIUM_ION_BATTERIES,
    checkbox: MARKET_SLUG.LITHIUM_ION_BATTERIES,
    hasSustainability: false,
    hasCapacityDatabase: true,
    capacityMethodologyType: METHODOLOGY_TYPE.DOWNLOAD,
    capacityRedirectUrl: '/methodologies/market-assessments/gigafactories',
    capacityService: MARKET_SERVICE.MARKET_ASSESSMENTS,
  },
  {
    name: 'Lithium',
    slug: MARKET_SLUG.LITHIUM,
    checkbox: MARKET_SLUG.LITHIUM,
    hasSustainability: true,
    hasCapacityDatabase: false,
    capacityMethodologyType: null,
    capacityRedirectUrl: null,
    capacityService: null,
  },
  {
    name: 'Cobalt',
    slug: MARKET_SLUG.COBALT,
    checkbox: MARKET_SLUG.COBALT,
    hasSustainability: true,
    hasCapacityDatabase: false,
    capacityMethodologyType: null,
    capacityRedirectUrl: null,
    capacityService: null,
  },
  {
    name: 'Nickel',
    slug: MARKET_SLUG.NICKEL,
    checkbox: MARKET_SLUG.NICKEL,
    hasSustainability: true,
    hasCapacityDatabase: false,
    capacityMethodologyType: null,
    capacityRedirectUrl: null,
    capacityService: null,
  },
  {
    name: 'Natural Graphite',
    slug: MARKET_SLUG.NATURAL_GRAPHITE,
    checkbox: MARKET_SLUG.NATURAL_GRAPHITE,
    hasSustainability: true,
    hasCapacityDatabase: false,
    capacityMethodologyType: null,
    capacityRedirectUrl: null,
    capacityService: null,
  },
  {
    name: 'Synthetic Graphite',
    slug: MARKET_SLUG.SYNTHETIC_GRAPHITE,
    checkbox: MARKET_SLUG.SYNTHETIC_GRAPHITE,
    hasSustainability: true,
    hasCapacityDatabase: false,
    capacityMethodologyType: null,
    capacityRedirectUrl: null,
    capacityService: null,
  },
  {
    name: 'Rare Earths',
    slug: MARKET_SLUG.RARE_EARTHS,
    checkbox: MARKET_SLUG.RARE_EARTHS,
    hasSustainability: true,
    hasCapacityDatabase: false,
    capacityMethodologyType: null,
    capacityRedirectUrl: null,
    capacityService: null,
  },
  {
    name: 'Manganese',
    slug: MARKET_SLUG.MANGANESE,
    checkbox: MARKET_SLUG.MANGANESE,
    hasSustainability: false,
    hasCapacityDatabase: false,
    capacityMethodologyType: null,
    capacityRedirectUrl: null,
    capacityService: null,
  },
  {
    name: 'Phosphate',
    slug: MARKET_SLUG.PHOSPHATE,
    checkbox: MARKET_SLUG.PHOSPHATE,
    hasSustainability: false,
    hasCapacityDatabase: false,
    capacityMethodologyType: null,
    capacityRedirectUrl: null,
    capacityService: null,
  },
  {
    name: 'Fluorspar',
    slug: MARKET_SLUG.FLUORSPAR,
    checkbox: MARKET_SLUG.FLUORSPAR,
    hasSustainability: false,
    hasCapacityDatabase: false,
    capacityMethodologyType: null,
    capacityRedirectUrl: null,
    capacityService: null,
  },
  {
    name: 'Silicon Anode',
    slug: MARKET_SLUG.SILICON_ANODE,
    checkbox: MARKET_SLUG.SILICON_ANODE,
    hasSustainability: false,
    hasCapacityDatabase: false,
    capacityMethodologyType: null,
    capacityRedirectUrl: null,
    capacityService: null,
  },
  {
    name: 'Recycling',
    slug: MARKET_SLUG.RECYCLING,
    checkbox: MARKET_SLUG.RECYCLING,
    hasSustainability: false,
    hasCapacityDatabase: false,
    capacityMethodologyType: null,
    capacityRedirectUrl: null,
    capacityService: null,
  },
]

export const MARKETS_WITH_CAPACITY_DATABASE = MARKETS.filter(
  (market) => market.hasCapacityDatabase,
)

export const MARKETS_WITH_SUSTAINABILITY = MARKETS.filter(
  (market) => market.hasSustainability,
)
